import { useCallback, useEffect, useRef } from 'preact/hooks'

/** Shifts start and end without any change to signal. */
const PERIODIC_RELOAD_MS = 60_000

/** Delay before reconnecting after `failures` consecutive failures: 1 s, doubling up to 30 s. */
export function reconnectDelay(failures: number): number {
  return Math.min(1000 * 2 ** failures, 30_000)
}

/** WebSocket URL of the API path `/api{path}`, on the same origin. */
export function socketUrl(location: { protocol: string; host: string }, path: string): string {
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${location.host}/api${path}`
}

type SocketHandlers = {
  onMessage: (data: string) => void
  /** Called on each connection; `reconnected` after a lost one, when signals may have been missed. */
  onOpen?: (reconnected: boolean) => void
}

/**
 * Keeps the WebSocket `/api{path}` open, reconnecting after a failure; returns a function that
 * sends a text message while it is open, and drops it otherwise.
 */
export function useSocket(path: string, handlers: SocketHandlers): (text: string) => void {
  const latest = useRef(handlers)
  latest.current = handlers
  const socket = useRef<WebSocket>(undefined)
  useEffect(() => {
    let retry: ReturnType<typeof setTimeout> | undefined
    let failures = 0
    let opened = false
    let stopped = false
    const connect = () => {
      const current = new WebSocket(socketUrl(window.location, path))
      socket.current = current
      current.onopen = () => {
        latest.current.onOpen?.(opened)
        opened = true
        failures = 0
      }
      current.onmessage = (event) => latest.current.onMessage(String(event.data))
      current.onclose = () => {
        if (!stopped) retry = setTimeout(connect, reconnectDelay(failures++))
      }
    }
    connect()
    return () => {
      stopped = true
      clearTimeout(retry)
      socket.current?.close()
      socket.current = undefined
    }
  }, [path])
  return useCallback((text: string) => {
    if (socket.current?.readyState === WebSocket.OPEN) socket.current.send(text)
  }, [])
}

/**
 * Calls `reload` whenever the WebSocket `/api{path}` signals a change, after a reconnection
 * (signals may have been missed), and every `periodMs` when given.
 */
export function useSignalReload(path: string, reload: () => void, periodMs?: number) {
  useSocket(path, {
    onMessage: reload,
    onOpen: (reconnected) => {
      if (reconnected) reload()
    },
  })
  useEffect(() => {
    if (periodMs === undefined) return
    const periodic = setInterval(reload, periodMs)
    return () => clearInterval(periodic)
  }, [reload, periodMs])
}

/** Calls `reload` whenever the event changes, for organizers, and every minute. */
export function useLiveReload(slug: string, reload: () => void) {
  useSignalReload(`/events/${slug}/live`, reload, PERIODIC_RELOAD_MS)
}
